import type { DataTableRowEventHandler } from '@/components/custom/erp/data-table-v2';
import type { TableSelectedCell } from '@/components/custom/erp/input-table-v2';
import { useTableRows } from '@/components/custom/erp/input-table-v2';
import type { FormFieldState } from '../../../hooks';

export type SelectedCellInfo = TableSelectedCell;

export function useInputTableRows<
  T extends {
    uuid: string;
    line?: number;
  }
>(
  initialRows: T[] = [],
  onChange?: (rows: T[]) => void,
  onFieldValueChange?: (row: T, field: string, value: BanHangValue) => Partial<T>,
  formState?: FormFieldState
) {
  const handleFieldValueChange = (row: T, field: string, value: BanHangValue): Partial<T> => {
    const extraFields = onFieldValueChange?.(row, field, value) ?? {};
    return extraFields;
  };

  const defaultRow = { uuid: 'temp-1' } as T;
  const processedInitialRows = initialRows && initialRows.length > 0 ? initialRows : [defaultRow];

  const {
    rows,
    setRows: setRowsInternal,
    selectedRowUuid,
    setSelectedRowUuid,
    selectedRow,
    setSelectedRow,
    selectedCell,
    setSelectedCell,
    handleRowClick: handleTableRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange: handleTableCellValueChange,
    handleAddRow: handleTableAddRow,
    handleDeleteRow: handleTableDeleteRow,
    handleCopyRow: handleTableCopyRow,
    handlePasteRow: handleTablePasteRow,
    handleMoveRow: handleTableMoveRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting
  } = useTableRows<T>({
    initialRows: processedInitialRows,
    defaultRows: 1,
    onFieldValueChange: handleFieldValueChange,
    defaultField: 'ma_dv'
  });

  const setRows = (newRows: T[]) => {
    setRowsInternal(newRows);
    onChange?.(newRows);
  };

  const handleRowClick: DataTableRowEventHandler<BanHangRecord> = params => {
    handleTableRowClick(params);
  };

  const handleCellValueChange = (rowUuid: string, field: string, newValue: T[keyof T]) => {
    handleTableCellValueChange(rowUuid, field, newValue);
    const updatedRows = rows.map(row => (row.uuid === rowUuid ? { ...row, [field]: newValue } : row));
    onChange?.(updatedRows);
  };

  const handleAddRow = () => {
    const newRow = {
      uuid: `temp-${Date.now()}`,
      dien_giai: formState?.dienGiai || '',
      ...(rows.length > 0 && rows[0].line !== undefined && { line: rows.length + 1 })
    } as unknown as T;

    handleTableAddRow();
    const updatedRows = [...rows, newRow];
    onChange?.(updatedRows);
  };

  const handleDeleteRow = () => {
    if (!selectedRowUuid) return;

    handleTableDeleteRow();
    const updatedRows = rows.filter((row: T) => row.uuid !== selectedRowUuid);
    const reorderedRows = updatedRows.map((row: T, index: number) => ({
      ...row,
      ...(row.line !== undefined && { line: index + 1 })
    }));
    onChange?.(reorderedRows);
  };

  const handleCopyRow = () => {
    if (!selectedRow) return;
    handleTableCopyRow();
  };

  const handlePasteRow = (clipboardText?: string) => {
    if (clipboardText) {
      handleTablePasteRow(clipboardText);
    } else {
      if (!selectedRow) return;
      handleTablePasteRow();
    }
  };

  const handleMoveRow = (direction: 'up' | 'down') => {
    if (!selectedRowUuid) return;
    handleTableMoveRow(direction);
    const currentIndex = rows.findIndex((row: T) => row.uuid === selectedRowUuid);
    if (currentIndex === -1) return;
    const newIndex = direction === 'up' ? Math.max(0, currentIndex - 1) : Math.min(rows.length - 1, currentIndex + 1);
    if (newIndex === currentIndex) return;
    const updatedRows = [...rows];
    const [movedRow] = updatedRows.splice(currentIndex, 1);
    updatedRows.splice(newIndex, 0, movedRow);
    updatedRows.forEach((row: T, index: number) => {
      if (row.line !== undefined) {
        (row as BanHangRecord).line = index + 1;
      }
    });
    onChange?.(updatedRows);
  };

  return {
    rows,
    setRows,
    selectedRowUuid,
    setSelectedRowUuid,
    selectedRow,
    setSelectedRow,
    selectedCell,
    setSelectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting
  };
}
